import { useMemo, useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useConversations } from "@/hooks/useMessages";
import { useUserNotifications } from "@/hooks/useUserNotifications";
import { useAuth } from "@/contexts/AuthContext";
import Sidebar, { isOverdue } from "@/components/hub/Sidebar";
import ChatWindow from "@/components/hub/ChatWindow";
import StatusBar from "@/components/hub/StatusBar";
import StatsDialog from "@/components/hub/StatsDialog";
import TeamDialog from "@/components/hub/TeamDialog";
import AgentStatsDialog from "@/components/hub/AgentStatsDialog";
import TemplatesDialog from "@/components/hub/TemplatesDialog";
import NewChatDialog from "@/components/hub/NewChatDialog";
import CampaignsDialog from "@/components/hub/CampaignsDialog";
import BotsDialog from "@/components/hub/BotsDialog";

export default function Dashboard() {
  const { user: me } = useAuth();
  useUserNotifications(me?.id);
  const { contacts, messagesByPhone, source, lastUpdate } = useConversations({
    soundOn: true,
  });
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [statsOpen, setStatsOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [agentStatsOpen, setAgentStatsOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [botsOpen, setBotsOpen] = useState(false);

  // Re-compute overdue contacts periodically
  const [slaTick, setSlaTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setSlaTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const filteredContacts = useMemo(() => {
    const s = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (filter === "mine") {
        if (c.assigned_to?.id !== me?.id || c.status === "resolved") return false;
      } else if (filter === "overdue") {
        if (!isOverdue(c, slaTick)) return false;
      } else if (filter !== "all" && c.status !== filter) {
        return false;
      }
      if (!s) return true;
      return (
        (c.name || "").toLowerCase().includes(s) ||
        (c.phone || "").toLowerCase().includes(s)
      );
    });
  }, [contacts, search, filter, me?.id, slaTick]);

  const activeMessages = selected ? messagesByPhone[selected] || [] : [];
  const activeContact = contacts.find((c) => c.phone === selected) || null;

  const handleSelect = async (phone) => {
    setSelected(phone);
    try {
      await api.patch(`/conversations/${phone}/read`);
    } catch (_) {}
  };

  const injectTest = async () => {
    try {
      const phone = "1555" + Math.floor(1000000 + Math.random() * 8999999);
      await api.post("/webhook/test", {
        from: phone,
        name: `Driver ${phone.slice(-4)}`,
        text: "Namaste sir, main pohonch gaya hoon 🙂",
      });
      toast.success("Test message sent");
    } catch (_) {
      toast.error("Failed to send test");
    }
  };

  return (
    <div
      className="h-full w-full flex flex-col overflow-hidden bg-[var(--bg)] text-[var(--fg)]"
      data-testid="dashboard-root"
    >
      <StatusBar
        source={source}
        lastUpdate={lastUpdate}
        onInjectTest={injectTest}
        onOpenStats={() => setStatsOpen(true)}
        onOpenTeam={() => setTeamOpen(true)}
        onOpenAgentStats={() => setAgentStatsOpen(true)}
        onOpenTemplates={() => setTemplatesOpen(true)}
        onOpenCampaigns={() => setCampaignsOpen(true)}
        onOpenBots={() => setBotsOpen(true)}
        onNewChat={() => setNewChatOpen(true)}
      />

      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          contacts={filteredContacts}
          allCount={contacts.length}
          counts={{
            all: contacts.length,
            overdue: contacts.filter((c) => isOverdue(c, slaTick)).length,
            mine: contacts.filter(
              (c) => c.assigned_to?.id === me?.id && c.status !== "resolved",
            ).length,
            new: contacts.filter((c) => c.status === "new").length,
            in_progress: contacts.filter((c) => c.status === "in_progress").length,
            resolved: contacts.filter((c) => c.status === "resolved").length,
          }}
          selected={selected}
          onSelect={handleSelect}
          search={search}
          setSearch={setSearch}
          filter={filter}
          setFilter={setFilter}
          currentUserId={me?.id}
          mobileHidden={!!selected}
        />
        <ChatWindow
          contact={activeContact}
          messages={activeMessages}
          onBack={() => setSelected(null)}
          mobileVisible={!!selected}
        />
      </div>

      <StatsDialog open={statsOpen} onClose={() => setStatsOpen(false)} />
      <TeamDialog open={teamOpen} onClose={() => setTeamOpen(false)} />
      <AgentStatsDialog open={agentStatsOpen} onOpenChange={setAgentStatsOpen} />
      <TemplatesDialog open={templatesOpen} onOpenChange={setTemplatesOpen} />
      <NewChatDialog open={newChatOpen} onClose={() => setNewChatOpen(false)} />
      <CampaignsDialog open={campaignsOpen} onOpenChange={setCampaignsOpen} />
      <BotsDialog open={botsOpen} onOpenChange={setBotsOpen} />
    </div>
  );
}
